import React, { FunctionComponent, useEffect, useState } from "react";
import { LookupPicker } from "@bg/shared/lib/components/pickers";
import { NewDocument } from "@bg/shared/lib/dal/models";
import { colors } from "@bg/shared/lib/theme";
import { gridContainerSpacing } from "@bg/shared/lib/theme/themes";
import {
  Button,
  darken,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  TextField,
  Tooltip,
} from "@material-ui/core";
import {
  FieldAndValue,
  PrimaryButton,
  SecondaryButton,
} from "@bg/shared/lib/components";
import { Cancel, Delete, Publish } from "@material-ui/icons";
import { ChangeEvent } from "react";
import SummaryPatientHeader from "../OrderHistory/OrderTable/SummaryPatientHeader";

const useStyles = makeStyles({
  root: {
    "& .MuiInputBase-input": {
      backgroundColor: colors.white,
    },
    "& .MuiSelect-root": {
      backgroundColor: colors.white,
    },
  },

  buttonContainer: {
    paddingTop: ".75em",
    paddingBottom: ".75em",
    width: "780px",
    backgroundColor: colors.grey,
  },
  floatRight: {
    float: "right",
  },
  closeWindowButton: {
    color: colors.candyRed,
  },
  selectButton: {
    color: colors.white,
    backgroundColor: colors.mediumBrown,
    "&:hover": {
      background: darken(colors.mediumBrown, 0.1),
    },
  },
  uploadButton: {
    textTransform: "none",
    color: colors.white,
    backgroundColor: colors.powderBlue,
    "&:hover": {
      background: darken(colors.powderBlue, 0.1),
    },
  },
  inputFileField: {
    display: "none",
  },
  messageText: {
    fontStyle: "italic",
  },
  fileRow: {
    marginBottom: "0.5em",
  },
  bottomBtnContainer: {
    paddingRight: "16px",
    backgroundColor: colors.grey,
  },
});

interface UploadMultiDocumentDialogProps {
  openDialog: boolean;
  orderInfo?: SummaryPatientHeader;
  closeDialog: () => void;
  uploadFiles: (document: NewDocument[], file: File[]) => void;
}

class NewDocWithErrors extends NewDocument {
  isFileUploaded: boolean;
  fileExceedsMaxSize: boolean;
  constructor() {
    super();
    this.isFileUploaded = false;
    this.fileExceedsMaxSize = false;
  }
}

const UploadMultiDocumentDialog: FunctionComponent<
  UploadMultiDocumentDialogProps
> = ({ openDialog, orderInfo, closeDialog, uploadFiles }) => {
  const [files, setFiles] = useState<File[]>([]);
  const fileRef = React.createRef<HTMLInputElement>();
  const [docs, setDocs] = useState<NewDocWithErrors[]>([]);
  const classes = useStyles();

  useEffect(() => {
    if (!openDialog) {
      setFiles([]);
      setDocs([new NewDocWithErrors()]);
    }
  }, [openDialog]);

  const handleFileUpload = () => {
    if (docs && files && files.length > 0) {
      uploadFiles(docs, files);
    }
  };

  const handleDocumentTypeChange = (
    event: ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    const idx = parseInt(event.target.name as string, 10);
    const typeId = parseInt(event.target.value as string, 10);
    const newDocs = Object.assign([] as NewDocWithErrors[], docs);
    newDocs[idx].typeId = typeId;
    setDocs(newDocs);
  };

  const handleDocumentInfoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const [index, name] = event.target.name.split("-");
    const idx = parseInt(index, 10);
    const newDocs = Object.assign([] as any[], docs);
    newDocs[idx][name] = event.target.value;
    setDocs(newDocs);
  };

  const handleSelectFileClick = () => {
    if (fileRef.current) {
      fileRef.current.click();
    }
  };

  const handleFileChange =
    (inputDoc: NewDocWithErrors) => (event: ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;
      if (fileList) {
        const newFiles: File[] = Object.assign([], files);
        const newDocs: NewDocWithErrors[] = Object.assign(
          [],
          docs.slice(0, -1)
        );
        // easiest way to iterate through FileList object
        for (let i = 0; i < fileList.length; i++) {
          const file = fileList[i];
          const newDoc = Object.assign({}, inputDoc);
          newDoc.title = file.name;
          newDoc.fileExceedsMaxSize = file.size > 20000000;
          newDoc.isFileUploaded = true; // I don't know what this is for
          newFiles.push(file);
          newDocs.push(newDoc);
        }
        newDocs.push(new NewDocWithErrors());
        setDocs(newDocs);
        setFiles(newFiles);
      }
    };

  const isUploadButtonDisabled = (): boolean => {
    return (
      docs.length <= 1 ||
      docs.some((doc) => doc.fileExceedsMaxSize) ||
      !docs.slice(0, -1).every((doc) => doc.isFileUploaded)
    );
  };

  const message = (doc: NewDocWithErrors, idx: number) => {
    const text = doc.fileExceedsMaxSize
      ? "File limit has been exceeded. Maximum size is 20MB."
      : !doc.typeId
      ? "Please select a document type."
      : !(files[idx] && files[idx].name)
      ? "Please select a file (20MB max)."
      : files[idx].name;

    return (
      <span
        style={doc.fileExceedsMaxSize ? { color: colors.candyRed } : {}}
        className={classes.messageText}
      >
        {text}
      </span>
    );
  };

  const selectFileDisabled = (idx?: number): boolean => {
    if (idx) {
      return !docs[idx].typeId;
    }
    return docs.some((doc) => !doc.typeId);
  };

  const handleRemoveUpload = (event: React.MouseEvent<HTMLButtonElement>) => {
    const idx = parseInt(event.currentTarget.name, 10);

    const newDocs = Object.assign([], docs);
    newDocs.splice(idx, 1);
    setDocs(newDocs);

    const newFiles = Object.assign([], files);
    newFiles.splice(idx, 1);
    setFiles(newFiles);
  };

  const renderAddOrDelete = (renderDelete: boolean, idx: number) => {
    if (renderDelete) {
      return (
        <IconButton
          name={`${idx}-remove`}
          onClick={handleRemoveUpload}
          size="small"
        >
          <Delete />
        </IconButton>
      );
    }
    return (
      <Tooltip
        title={
          selectFileDisabled(idx)
            ? "Select document type"
            : "Select document(s) to upload"
        }
        aria-label="addToUpload"
      >
        <span>
          <IconButton
            onClick={handleSelectFileClick}
            className={classes.selectButton}
            disabled={selectFileDisabled()}
            size="small"
          >
            <Publish />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const fileToBeUploaded = (doc: NewDocWithErrors, idx: number) => {
    return (
      <div key={idx} className={classes.fileRow}>
        <Grid container spacing={gridContainerSpacing} alignItems="flex-end">
          <Grid item xs={12} md={4} classes={{ root: classes.root }}>
            <LookupPicker
              type="Document Type"
              fullWidth
              id="document-type"
              name={`${idx}-typeId`}
              value={doc.typeId}
              onChange={handleDocumentTypeChange}
              required
            />
          </Grid>
          <Grid item xs={11} md={7}>
            <TextField
              classes={{ root: classes.root }}
              label="Description"
              id="document-description"
              fullWidth
              name={`${idx}-description`}
              value={doc.description}
              onChange={handleDocumentInfoChange}
            />
          </Grid>
          <Grid item>{renderAddOrDelete(doc.isFileUploaded, idx)}</Grid>
        </Grid>
        <Grid item xs={12} md={8}>
          <div>{message(doc, idx)}</div>
        </Grid>
        <input
          id="document-file"
          type="file"
          multiple={true}
          className={classes.inputFileField}
          onChange={handleFileChange(doc)}
          ref={fileRef}
        />
      </div>
    );
  };

  return (
    <Dialog maxWidth="lg" open={openDialog}>
      <div className={classes.buttonContainer}>
        <Button className={classes.floatRight} onClick={closeDialog}>
          <Cancel className={classes.closeWindowButton} fontSize="medium" />
        </Button>
        <DialogTitle>Upload Document{docs.length > 1 && "s"}</DialogTitle>
        <DialogContent>
          {orderInfo && (
            <Grid container spacing={gridContainerSpacing}>
              {(orderInfo.firstName || orderInfo.lastName) && (
                <Grid item xs={4}>
                  <FieldAndValue
                    field="Name"
                    value={
                      orderInfo &&
                      `${orderInfo.firstName} ${orderInfo.lastName}`
                    }
                  />
                </Grid>
              )}
              {orderInfo.dobStr && (
                <Grid item xs={2}>
                  <FieldAndValue field="DOB" value={orderInfo.dobStr} />
                </Grid>
              )}
              {!!orderInfo.labNumber && (
                <Grid item xs={2}>
                  <FieldAndValue
                    field="Lab Number"
                    value={orderInfo && orderInfo.labNumber}
                  />
                </Grid>
              )}
              {orderInfo.testName && (
                <Grid item xs={4}>
                  <FieldAndValue
                    field="Test Name"
                    value={orderInfo && orderInfo.testName}
                  />
                </Grid>
              )}
            </Grid>
          )}
          {docs.map((doc, idx) => fileToBeUploaded(doc, idx))}
        </DialogContent>
      </div>
      <DialogActions className={classes.bottomBtnContainer}>
        <PrimaryButton
          onClick={handleFileUpload}
          disabled={isUploadButtonDisabled()}
        >
          {`Upload File${files.length > 1 ? "s" : ""}`}
        </PrimaryButton>
        <SecondaryButton onClick={closeDialog}>Close</SecondaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default UploadMultiDocumentDialog;
